import React, { useState } from "react";
import { Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useGetLeadsPaginatedQuery } from "@/redux/rtk/features/leads/leadsApi";
import { useGetApplicationsQuery } from "@/redux/rtk/features/application/applicationApi";
import { useGetDealsPaginatedQuery } from "@/redux/rtk/features/CRM/deal/dealApi";

export default function DrillDownTable({ type, filters, data: injectedData }) {
  const { t } = useTranslation();
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    count: 10,
    ...filters,
  });

  const { data: leadData, isLoading: leadLoading } = useGetLeadsPaginatedQuery(
    type === "leads" && !injectedData ? pageConfig : null,
    { skip: type !== "leads" || !!injectedData }
  );

  const { data: appData, isLoading: appLoading } = useGetApplicationsQuery(
    type === "applications" && !injectedData ? pageConfig : null,
    { skip: type !== "applications" || !!injectedData }
  );

  const { data: dealData, isLoading: dealLoading } = useGetDealsPaginatedQuery(
    type === "deals" && !injectedData ? pageConfig : null,
    { skip: type !== "deals" || !!injectedData }
  );

  // If injectedData is provided, use it. Otherwise use the fetched data.
  const isUsingInjectedData = !!injectedData;
  const rawData = isUsingInjectedData 
    ? injectedData 
    : (type === "leads" ? leadData?.getAllLead : (type === "applications" ? appData?.getAllApplication : dealData?.getAllDeal));
  
  const total = isUsingInjectedData 
    ? injectedData.length 
    : (type === "leads" ? leadData?.totalLead : (type === "applications" ? appData?.totalApplication : dealData?.totalDeal));
  
  const loading = isUsingInjectedData ? false : (type === "leads" ? leadLoading : (type === "applications" ? appLoading : dealLoading));

  // Local pagination for injected data
  const data = isUsingInjectedData 
    ? injectedData.slice((pageConfig.page - 1) * pageConfig.count, pageConfig.page * pageConfig.count)
    : rawData;

  if (data && !loading) {
    console.log(`--- DRILLDOWN DATA DEBUG [${type}] ---`, data);
  }

  const leadColumns = [
    {
      title: t("common.id"),
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("lead.name"),
      key: "name",
      render: (_, item) => (
        <Link to={`/admin/lead/${item.id}`} target="_blank">
          {item.firstName} {item.lastName}
        </Link>
      ),
    },
    {
      title: t("common.email"),
      dataIndex: "email",
      key: "email",
    },
    {
        title: t("lead.leadStatus"),
        key: "leadStatus",
        dataIndex: "leadStatusName",
        render: (val, item) => val || item.leadStatus || "-",
    },
    {
      title: t("common.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const appColumns = [
    {
      title: t("common.id"),
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("application.student"),
      key: "student",
      render: (_, record) => (
        <Link to={`/admin/application/${record.id}`} target="_blank">
          {record.deal?.firstName || record.lead?.firstName || "-"} {record.deal?.lastName || record.lead?.lastName || ""}
        </Link>
      ),
    },
    {
      title: t("application.university"),
      key: "university",
      render: (_, record) => record.university?.universityName || record.universityName || "-",
    },
    {
      title: t("application.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color="blue">{status}</Tag>,
    },
    {
        title: t("application.paymentStatus"),
        dataIndex: "paymentStatus",
        key: "paymentStatus",
        render: (status) => (
            <Tag color={status === 'through' ? 'cyan' : 'orange'}>
                {status?.toUpperCase() || 'PENDING'}
            </Tag>
        ),
    },
    {
      title: t("common.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const dealColumns = [
    {
      title: t("common.id"),
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("deal.dealName"),
      key: "dealName",
      render: (_, record) => (
        <Link to={`/admin/deal/${record.id}`} target="_blank">
          {record.dealName || `${record.firstName} ${record.lastName}`}
        </Link>
      ),
    },
    {
      title: t("deal.stageName"),
      dataIndex: "stageName",
      key: "stageName",
      render: (val) => <Tag color="green">{val}</Tag>,
    },
    {
        title: t("common.probability"),
        dataIndex: "probability",
        key: "probability",
        render: (val) => `${val}%`,
    },
    {
        title: t("application.major"),
        dataIndex: "majorName",
        key: "majorName",
        render: (val) => val || "-",
    },
    {
      title: t("common.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const studentColumns = [
    {
      title: t("common.id"),
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("lead.name"),
      key: "name",
      render: (_, item) => (
        <Link to={`/admin/${item.type || 'lead'}/${item.id}`} target="_blank">
          {item.firstName} {item.lastName}
        </Link>
      ),
    },
    {
      title: t("common.email"),
      dataIndex: "email",
      key: "email",
      render: (val) => val || "-",
    },
    {
        title: t("common.status"),
        key: "status",
        dataIndex: "status",
        render: (val) => (
            <Tag color={val === 'Deal' ? 'green' : 'blue'}>
                {val || "New"}
            </Tag>
        ),
    },
    {
      title: t("common.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => date ? new Date(date).toLocaleDateString() : "-",
    },
  ];

  const columns = type === "leads" ? leadColumns : (type === "applications" ? appColumns : (type === "students" ? studentColumns : dealColumns));

  return (
    <Table
      dataSource={data}
      columns={columns}
      loading={loading}
      pagination={{
        current: pageConfig.page,
        pageSize: pageConfig.count,
        total: total,
        onChange: (page, pageSize) => {
          setPageConfig((prev) => ({ ...prev, page, count: pageSize }));
        },
      }}
      rowKey="id"
      size="middle"
    />
  );
}
